import {
  Box,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  MenuItem,
  Theme,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import makeStyles from "@mui/styles/makeStyles";
import { replaceNodesBreadcrumb } from "common/breadcrumb/store";
import FormModal from "common/components/FormModal";
import useWorkspaces from "common/hooks/useWorkspaces";
import { addError } from "common/store/messagesSlice";
import routes from "developer/routes";
import { useEffect, useMemo, useState } from "react";
import { Field } from "react-final-form";
import { useHistory } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  FormDefinition,
  createFormDefinition,
} from "../store/formDefinitionsSlice";
import FormsCrudTable from "./crudTable";
import FormsCrudToolbar from "./toolbar";

const useStyles = makeStyles((theme: Theme) => ({
  error: {
    color: "red",
  },
}));

const FormsCRUD = () => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const hashWorkspaces = useWorkspaces();
  const history = useHistory();

  const { workspaceIdSelected } = useAppSelector(
    (state) => state.developer.formsCRUD
  );

  const workspaces = useMemo(() => {
    return Object.values(hashWorkspaces);
  }, [hashWorkspaces]);

  useEffect(() => {
    dispatch(
      replaceNodesBreadcrumb([
        { text: "Form Definitions", url: routes.FORM_DEFINITIONS },
      ])
    );
  }, [dispatch]);

  const handleClickCreateForm = () => {
    setIsModalOpen(true);
  };

  const cancelCreateForm = () => {
    setIsModalOpen(false);
  };

  const confirmCreateForm = async (values: FormDefinition) => {
    setIsModalOpen(false);

    try {
      const response = await dispatch(createFormDefinition(values)).unwrap();
      const redirectRoute = routes.FORM_DEFINITIONS.replace(
        ":formDefinitionId",
        response.id
      );
      history.push(redirectRoute);
    } catch (error: any) {
      console.error(error);
      dispatch(addError({ message: error.message }));
    }
  };

  const required = (value: string) => (value ? undefined : "Name is required");

  const requiredWorkspace = (value: string) =>
    value ? undefined : "Workspace is Required";

  return (
    <>
      <FormModal<FormDefinition>
        open={isModalOpen}
        onSubmit={confirmCreateForm}
        onCancel={cancelCreateForm}
        title="Create form definition"
        confirmLabel="Create"
        cancelLabel="Cancel"
        initialValues={{
          id: "",
          name: "",
          description: "",
          workspaceId: workspaceIdSelected !== "All" ? workspaceIdSelected : "",
          disabled: false,
          displayName: "",
          displayDescription: "",
          connect: false,
        }}
      >
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Field
              id="name"
              name="name"
              placeholder="Form name goes here"
              validate={required}
            >
              {({ input, meta }) => (
                <div>
                  <TextField
                    name={input.name}
                    value={input.value}
                    onChange={input.onChange}
                    fullWidth
                    size="small"
                    label="Name"
                    variant="filled"
                  />
                  {meta.error && meta.touched && (
                    <span className={classes.error}>{meta.error}</span>
                  )}
                </div>
              )}
            </Field>
          </Grid>
          <Grid item>
            <Field id="description" name="description">
              {({ input }) => (
                <div>
                  <TextField
                    multiline
                    minRows={3}
                    maxRows={Infinity}
                    name={input.name}
                    size="small"
                    value={input.value}
                    fullWidth
                    onChange={input.onChange}
                    label="Description"
                    variant="filled"
                  />
                </div>
              )}
            </Field>
          </Grid>
          <Grid item>
            <Field
              id="workspaceId"
              name="workspaceId"
              validate={requiredWorkspace}
            >
              {({ input, meta }) => (
                <div>
                  <TextField
                    select
                    fullWidth
                    id="select-workspace"
                    value={input.value}
                    onChange={input.onChange}
                    label="Workspace"
                    name={input.name}
                    variant="filled"
                    size="small"
                  >
                    {workspaces.map((item) => (
                      <MenuItem key={item.id!} value={item.id!}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  {meta.error && meta.touched && (
                    <span className={classes.error}>{meta.error}</span>
                  )}
                </div>
              )}
            </Field>
          </Grid>
          <Grid item>
            <Divider />
          </Grid>
          <Grid item>
            <Typography variant="subtitle2">Connect</Typography>
          </Grid>
          <Grid item>
            <Field id="displayName" name="displayName">
              {({ input, meta }) => (
                <div>
                  <TextField
                    name={input.name}
                    value={input.value}
                    onChange={input.onChange}
                    fullWidth
                    label="Display Name"
                    size="small"
                    variant="filled"
                  />
                  {meta.error && meta.touched && (
                    <span className={classes.error}>{meta.error}</span>
                  )}
                </div>
              )}
            </Field>
          </Grid>
          <Grid item>
            <Field id="displayDescription" name="displayDescription">
              {({ input }) => (
                <div>
                  <TextField
                    multiline
                    minRows={3}
                    maxRows={Infinity}
                    name={input.name}
                    value={input.value}
                    size="small"
                    onChange={input.onChange}
                    fullWidth
                    label="Display Description"
                    variant="filled"
                  />
                </div>
              )}
            </Field>
          </Grid>
          <Grid item>
            <Field id="connect" name="connect">
              {({ input }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={input.value}
                      size="small"
                      color="primary"
                    />
                  }
                  label="Connect Only"
                  name={input.name}
                  value={input.value}
                  onChange={input.onChange}
                />
              )}
            </Field>
          </Grid>
        </Grid>
      </FormModal>
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box>
          <FormsCrudToolbar
            workspaces={workspaces}
            workspaceIdSelected={workspaceIdSelected}
            onClickCreateForm={handleClickCreateForm}
          />
        </Box>
        <Box sx={{ overflowY: "auto" }}>
          <Container maxWidth="xl">
            <FormsCrudTable workspaceIdSelected={workspaceIdSelected} />
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default FormsCRUD;
